import React, { useCallback, useRef } from 'react';
import {
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  View,
  TouchableOpacity,
} from 'react-native';
import { RouteProp, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, AppDispatch } from '@/store';
import { getStaffMemberClasses } from '@/store/slices/staffClassesSlice';
import { ClassesStackParamList } from '@/types/navigation';
import { MyClass } from '@/types/api';
import { useChicagoSeconds } from '@/hooks/useChicagoSeconds';
import { chicagoToday } from '@/utils/scheduleWeek';
import { addDays } from '@/utils/rotations';
import { ClassDay, DayPicker } from '@/components/classes/ClassDay';
import { colors, font, space, radius } from '@/theme/chalkCourt';

type Nav = StackNavigationProp<ClassesStackParamList, 'StaffMember'>;

/** Admin: one staff member's day, exactly as they see it in "My Classes". */
const StaffMemberScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<Nav>();
  const { params } = useRoute<RouteProp<ClassesStackParamList, 'StaffMember'>>();
  const { member, memberRequest, memberLoading, memberError } = useSelector(
    (s: RootState) => s.staffClasses
  );

  // The day this screen asked for last: starts at the day picked in the list,
  // then follows the arrows — so returning from a class keeps the day. Scoped
  // to this screen instance: the slice's request outlives a popped screen, so
  // a later visit to the same person must not inherit it.
  const shownDayRef = useRef<string | null>(params.date);
  const dispatchedHere = useRef(false);
  const load = (date: string | null) => {
    dispatchedHere.current = true;
    shownDayRef.current = date;
    dispatch(getStaffMemberClasses({ employeeId: params.employeeId, date }));
  };
  const loadRef = useRef(load);
  loadRef.current = load;
  useFocusEffect(
    useCallback(() => {
      loadRef.current(shownDayRef.current);
    }, [])
  );

  // Show held data only when it is the day this screen asked for this person
  // (not another visit's, nor the previous day while stepping).
  const requestedDate = shownDayRef.current;
  const mine = dispatchedHere.current && !!memberRequest?.startsWith(`${params.employeeId}|`);
  const data = member && mine && member.date === (requestedDate ?? member.today) ? member : null;

  const isToday = !!data && data.date === data.today && data.today === chicagoToday();
  const nowSec = useChicagoSeconds(isToday);
  const target = data ? requestedDate ?? data.today : null;
  const goDay = (day: string) => data && load(day === data.today ? null : day);
  const openClass = (cls: MyClass) =>
    navigation.navigate('ClassDetail', { classKey: cls.key, title: cls.name, source: 'staff' });

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={memberLoading && !!data}
          onRefresh={() => load(requestedDate)}
          tintColor={colors.accent}
          colors={[colors.accent]}
        />
      }>
      <Text style={styles.name}>{data?.employeeName ?? params.name}</Text>
      <DayPicker
        date={data ? data.date : null}
        isToday={isToday}
        canPrev={!!data && target! > data.weekStart}
        canNext={!!data && target! < data.weekEnd}
        onPrev={() => target && goDay(addDays(target, -1))}
        onNext={() => target && goDay(addDays(target, 1))}
        onToday={() => data && goDay(data.today)}
      />
      {!data && memberLoading && <ActivityIndicator style={styles.loading} color={colors.accent} />}
      {memberError && (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{memberError}</Text>
          <TouchableOpacity onPress={() => load(requestedDate)}>
            <Text style={styles.retry}>Try again</Text>
          </TouchableOpacity>
          {/* e.g. a day from last week after the Sunday rollover (400) */}
          {requestedDate && (
            <TouchableOpacity onPress={() => load(null)} testID="member-today">
              <Text style={styles.retry}>Show today</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
      {data && (
        <ClassDay
          data={data}
          isToday={isToday}
          nowSec={nowSec}
          onOpen={openClass}
          doneText="Done teaching for today."
          emptyText="No classes on their schedule for this day."
        />
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },
  name: {
    fontFamily: font.display,
    fontSize: 36,
    lineHeight: 38,
    color: colors.ink,
    marginBottom: space.sm,
  },
  loading: { marginTop: space.xl },
  errorBox: {
    backgroundColor: colors.accentSoft,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.lg,
  },
  errorText: { fontFamily: font.body, fontSize: 14, color: colors.accentDark },
  retry: { fontFamily: font.bodyBold, fontSize: 14, color: colors.accentDark, marginTop: space.sm },
});

export default StaffMemberScreen;
